import { useEffect, useState } from 'react'
import { Calculator, ExternalLink, X } from 'lucide-react'
import {
  ApiError,
  CreatorBusinessDetail,
  CreatorBusinessItem,
  getCreatorBusinessDetail,
  PRICE_GROUPS,
} from './creatorBusiness'

const ATTRS: { key: keyof CreatorBusinessDetail; label: string }[] = [
  { key: 'personalColor', label: '퍼스널 컬러' },
  { key: 'skinType', label: '피부 타입' },
  { key: 'skinWorry', label: '피부 고민' },
  { key: 'preferenceIngredient', label: '선호 성분' },
  { key: 'dislikeIngredient', label: '비선호 성분' },
  { key: 'channelScale', label: '채널 규모' },
  { key: 'businessType', label: '비즈니스 타입' },
  { key: 'contentsGenre', label: '콘텐츠 장르' },
  { key: 'language', label: '구사 가능 언어' },
  { key: 'contractType', label: '계약 형태' },
  { key: 'creatorLabel', label: '레이블' },
  { key: 'manager', label: '매니저' },
  { key: 'salesforceUpdatedAt', label: '업데이트 일시' },
]

function Row({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line-hairline py-2">
      <span className="lf-caption text-fg-3">{label}</span>
      <span className="lf-body-sm text-right text-fg-1">{value && value !== '-' ? value : '-'}</span>
    </div>
  )
}

export default function CreatorDetailSheet({
  open,
  onOpenChange,
  creator,
  onAddToCalculator,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  creator: CreatorBusinessItem | null
  onAddToCalculator: (c: CreatorBusinessItem) => void
}) {
  const [detail, setDetail] = useState<CreatorBusinessDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !creator) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setDetail(null)
    getCreatorBusinessDetail(creator.salesforceCreatorId)
      .then((d) => !cancelled && setDetail(d))
      .catch((e) => !cancelled && setError((e as ApiError).message))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [open, creator])

  if (!open || !creator) return null
  const d = detail ?? (creator as CreatorBusinessDetail)

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-brand-brown/40" onClick={() => onOpenChange(false)} />
      <div className="relative flex h-full w-full max-w-md flex-col bg-surface-base shadow-lf-pop">
        {/* header */}
        <div className="flex items-center gap-3 border-b border-line-soft px-5 py-4">
          {d.creatorImageUrl && d.creatorImageUrl !== '-' ? (
            <img src={d.creatorImageUrl} alt={d.name} className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-muted lf-body-sm text-fg-2">
              {d.name?.[0] ?? '?'}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="lf-h4 truncate text-fg-1">{d.name}</div>
            <div className="lf-caption text-fg-3">{d.creatorLabel || '-'} · {d.contractType || '-'}</div>
          </div>
          <button type="button" onClick={() => onOpenChange(false)} className="text-fg-3 hover:text-fg-1">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {loading && <div className="lf-body-sm text-fg-3">불러오는 중…</div>}
          {error && <div className="lf-body-sm text-brand-pink">{error}</div>}

          {/* 채널 */}
          <div className="lf-eyebrow mb-1 text-fg-3">채널</div>
          <Row label="YouTube 구독자" value={d.youtubeSubscriberCount} />
          <Row label="YouTube 채널" value={d.youtubeChannelName} />
          <Row label="Instagram 팔로워" value={d.instagramFollowerCount} />
          <Row label="Instagram 채널" value={d.instagramChannelName} />

          {/* 단가 */}
          {PRICE_GROUPS.map((g) => (
            <div key={g.group} className="mt-4">
              <div className="lf-eyebrow mb-1 text-fg-3">{g.group}</div>
              {g.items.map((it) => (
                <Row key={it.key} label={it.label} value={d[it.key] as string | undefined} />
              ))}
            </div>
          ))}

          {/* 속성 */}
          <div className="mt-4">
            <div className="lf-eyebrow mb-1 text-fg-3">속성 · 세일즈</div>
            {ATTRS.map((a) => (
              <Row key={String(a.key)} label={a.label} value={d[a.key] as string | undefined} />
            ))}
          </div>

          {(d.youtubeLink || d.instagramLink) && (
            <div className="mt-4 flex gap-3">
              {d.youtubeLink && d.youtubeLink !== '-' && (
                <a href={d.youtubeLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 lf-caption text-brand-blue">
                  <ExternalLink size={13} /> YouTube
                </a>
              )}
              {d.instagramLink && d.instagramLink !== '-' && (
                <a href={d.instagramLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 lf-caption text-brand-blue">
                  <ExternalLink size={13} /> Instagram
                </a>
              )}
            </div>
          )}
        </div>

        {/* footer */}
        <div className="border-t border-line-soft p-4">
          <button
            type="button"
            onClick={() => onAddToCalculator(creator)}
            className="flex w-full items-center justify-center gap-1.5 rounded-r-1 bg-brand-pink px-3 py-2.5 lf-body-sm font-medium text-brand-brown hover:opacity-90"
          >
            <Calculator size={16} /> 견적 계산기에 담기
          </button>
        </div>
      </div>
    </div>
  )
}
